import React, { useEffect, useState } from 'react';
import styles from './index.module.less';
import { Form, Input, App, Checkbox, Button, Divider } from 'antd';
import Link from 'next/link';
import Image from 'next/image';
import { isEmailValid } from '@/shared/utils';
import { ga_logEvent } from '@/shared/ga';
import { useUserProvider } from '@/context/UserProvider';
// ✅ 优化：移除 Firebase 同步导入（146.5KB），改为按需动态加载
// import {
//   getAuth,
//   signInWithPopup,
//   GoogleAuthProvider,
//   createUserWithEmailAndPassword,
//   signInWithEmailAndPassword,
//   signInWithRedirect,
//   sendEmailVerification,
//   signOut,
// } from 'firebase/auth';
import { useRouter } from 'next/router';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';

import { userUpdate, getUserInfoApi, sendEmailVerificationCode } from '@/lib/service';
import { EventName } from '@/shared/event-types';
import { useTranslation } from 'next-i18next';
import { useConfigProvider } from '@/context/ConfigProvider';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { logRegisterModelGA } from '@/utils/loginModelGA';
import EmailRegisterInput from '../EmailRegisterInput';

const Login: React.FC<any> = () => {
  // ✅ 优化：移除 GoogleAuthProvider 的提前初始化
  // const provider = new GoogleAuthProvider();
  const { width } = useScreenSize();
  const { isIPhone } = useConfigProvider();

  // ✅ 优化：动态导入 Firebase（首屏不加载 146.5KB，点击时才加载）
  const onGoogleAuth = async () => {
    const { getAuth, signInWithPopup, signInWithRedirect, GoogleAuthProvider } = await import('firebase/auth');
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    if (width < 900 && !isIPhone) {
      signInWithRedirect(auth, provider);
    } else {
      signInWithPopup(auth, provider)
        .then((result) => {
          // 只使用 ID Token，accessToken 未使用因此不存储
          result.user.getIdToken().then((id) => {
            getToken(id, 'google');
            ga_logEvent(EventName.login, { method: 'Google' });
          });
        })
        .catch(() => {
          onAddToken(undefined);
          message.error(t('Google_Login_Failed_Error_Msg'));
        });
    }
  };
  const { message } = App.useApp();
  const { t } = useTranslation('common');
  const [isReadProtocol, setIsReadProtocol] = useState(true);

  const router = useRouter();
  const [isMailReception, setIsMailReception] = useState(true);

  const [loading, setLoading] = useState(false);

  // 是否是输入验证码界面
  const [isInputVerificationCode, setIsInputVerificationCode] = useState(false);
  // 表单数据
  const [formData, setFormData] = useState();

  const getToken = async (id: string, signInMethod: string) => {
    const res: any = await userUpdate(id, router.locale);

    if (res?.data?.first_login) {
      if (res.code == 0) {
        ga_logEvent(EventName.sign_up);
      } else {
        ga_logEvent(EventName.user_register_Fail);
      }
    }

    if (res.code == 0) {
      localStorage.removeItem('source');
      onAddToken(res.data.token, signInMethod, id);
      getUserInfoApi().then((res) => {
        setUserInfo(res.data);
        localStorage.setItem('uif', JSON.stringify(res.data));
      });
      onOpenLoginModal(false);
      onChangedLoginResult(true);
      ga_logEvent(EventName.user_Login_Success);
    } else {
      message.error(res.message);
    }
    setLoading(false);
  };

  const { onOpenLoginModal, isLoginView, onChangedLoginView, onChangedLoginResult, onAddToken, setUserInfo } =
    useUserProvider();

  // ✅ 优化：动态导入 Firebase（邮箱登录/注册时才加载）
  const onFinish = async (values: any) => {
    const { getAuth, signInWithEmailAndPassword, sendEmailVerification, signOut } = await import('firebase/auth');
    const auth = getAuth();
    setLoading(true);

    if (isLoginView) {
      signInWithEmailAndPassword(auth, values?.email, values?.password)
        .then((result) => {
          const tempUser = result?.user;
          if (tempUser?.emailVerified) {
            tempUser?.getIdToken().then((id) => {
              getToken(id, 'email');
              ga_logEvent(EventName.login, { method: 'Email' });
            });
          } else {
            sendEmailVerification(result?.user, {
              url: process.env.NEXT_APP_WEB_HOST,
            })
              ?.then()
              .then(() => {
                message.success(
                  t('A verification email has been sent to x111x. Please check your inbox.').replace(
                    'x111x',
                    values?.email,
                  ),
                );
                onChangedLoginView(true);
              })
              .catch(() => {
                message.success(
                  t('A verification email has been sent to x111x. Please check your inbox.').replace(
                    'x111x',
                    values?.email,
                  ),
                );
              })
              .finally(() => {
                setLoading(false);
                signOut(getAuth());
                onAddToken(undefined);
              });
          }
        })
        .catch(() => {
          onAddToken(undefined);
          message.error(t('Login_Failed_Error_Msg'));
          setLoading(false);
        });
    } else {
      if (!isReadProtocol) {
        message.warning(t('Non-consent agreement'));
        setLoading(false);
        return;
      }
      // 发送邮箱验证码
      sendEmailVerificationCode(values?.email, 'register')
        .then((res) => {
          // 发送成功或频繁发送时，都进入输入验证码页面
          if (res.code == 0 || res.code == 10001) {
            setIsInputVerificationCode(true);
            setFormData(values);
            if (res.code == 0) message.success(t('Please enter the 6-digit verification code sent to your email.'));
          } else if (res.code == 10004) {
            message.error(t('User_Already_Exists'));
          } else {
            message.error(res.message);
          }
        })
        .catch((error) => {
          message.error(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (router.query.type) {
      onChangedLoginView(false);
    }
  }, []);

  // 邮箱页面返回按钮的点击事件
  const onBackToRegister = () => {
    setIsInputVerificationCode(false);
  };

  // 注册成功后的回调事件
  const onSuccess = () => {
    setIsInputVerificationCode(false);
    setFormData(undefined);
    router.push('/');
  };

  return (
    <div className={styles.container}>
      {isInputVerificationCode ? (
        <>
          {/* 输入验证码界面 */}
          <EmailRegisterInput formData={formData} onBackToRegister={onBackToRegister} onSuccess={onSuccess} />
        </>
      ) : (
        <>
          <h1>{isLoginView ? t('Sign in') : t('SignUp')}</h1>
          <div className={styles.form}>
            <Form
              key={formData ? 'with-data' : 'empty'}
              name="normal_login"
              className={styles.homeContent}
              initialValues={{
                email: (formData as any)?.email,
                password: (formData as any)?.password,
                repassword: (formData as any)?.repassword,
                remember: true,
              }}
              onFinish={onFinish}
              style={{ padding: '20px 0px' }}
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: t('pleaseInputYourEmail') },
                  () => ({
                    validator(_, value) {
                      if (!value || isEmailValid(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('pleaseInputYourEmail')));
                    },
                  }),
                ]}
              >
                <Input
                  className={styles.inputItem}
                  prefix={<UserOutlined style={{ color: '#B3B3B3', fontSize: '20px' }} />}
                  placeholder={t('EmailAddress')}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: t('pleaseInputYourPassword') },
                  () => ({
                    validator(_, value) {
                      if (!value || (`${value}`?.length >= 6 && `${value}`?.length <= 16)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('PasswordShouldBe_6_16_Characters')));
                    },
                  }),
                ]}
              >
                <Input
                  className={styles.inputItem}
                  prefix={<LockOutlined style={{ color: '#B3B3B3', fontSize: '20px' }} />}
                  type="password"
                  placeholder={t('Password')}
                />
              </Form.Item>
              {!isLoginView && (
                <Form.Item
                  name="repassword"
                  rules={[
                    { required: true },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('theNewPasswordThatYouEnteredDoNotMatch')));
                      },
                    }),
                  ]}
                >
                  <Input
                    className={styles.inputItem}
                    prefix={<LockOutlined style={{ color: '#B3B3B3', fontSize: '20px' }} />}
                    type="password"
                    placeholder={t('ConfirmPassword')}
                  />
                </Form.Item>
              )}

              {!isLoginView && (
                <>
                  <div className={styles.agreeDesc}>
                    <Checkbox checked={isReadProtocol} onChange={() => setIsReadProtocol(!isReadProtocol)} />
                    <div>
                      <span>{t('ByClickingContinue_Desc')}</span>
                      <span>
                        <Link href={'/terms-us'} className={styles.link} onClick={() => onOpenLoginModal(false)}>
                          {t('Terms')},
                        </Link>
                      </span>
                      <span>
                        <Link href={'/privacy-policy'} className={styles.link} onClick={() => onOpenLoginModal(false)}>
                          {t('privacyPolicy')}
                        </Link>
                      </span>
                    </div>
                  </div>
                  <div className={styles.agreeDesc}>
                    <Checkbox checked={isMailReception} onChange={() => setIsMailReception(!isMailReception)} />
                    <span>{t('EmailNotifications')}</span>
                  </div>
                </>
              )}

              <Form.Item name="btn">
                <Button type="primary" htmlType="submit" className={styles.loginFormButton} loading={loading}>
                  {isLoginView ? t('Next') : t('CreateAnAccount')}
                </Button>
              </Form.Item>

              {isLoginView && (
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox className={styles.staySignedIn}>{t('StaySignedIn')}</Checkbox>
                  <Link
                    className={styles.forgotItem}
                    href="/forgot-password"
                    onClick={() => {
                      onOpenLoginModal(false);
                      ga_logEvent(EventName.login_Forgot_Password);
                    }}
                  >
                    {t('ForgotPassword')}?
                  </Link>
                </Form.Item>
              )}

              {isLoginView ? (
                <Form.Item name="googlebtn">
                  <Button
                    className={styles.createFormButton}
                    onClick={() => {
                      onChangedLoginView(false);
                      logRegisterModelGA();
                    }}
                  >
                    {t('CreateAnAccount')}
                  </Button>
                </Form.Item>
              ) : (
                <Form.Item name="googlebtn">
                  <Button className={styles.createFormButton} onClick={() => onChangedLoginView(true)}>
                    {t('Back to login')}
                  </Button>
                </Form.Item>
              )}

              <Form.Item name="">
                <Divider className={styles.googleLoginText}>
                  {isLoginView && <span>{t('Login')}</span>}
                  {isLoginView && <span> {t('with others')}</span>}
                  {!isLoginView && <span> {t('or create an account with')}</span>}
                </Divider>

                <Button className={styles.googleFormButton} onClick={onGoogleAuth}>
                  <Image
                    src={`/images/login/google_icon.webp`}
                    width={20}
                    height={20}
                    alt={'google login'}
                    loading="lazy"
                  />
                  <span>{t('Login with')}</span>
                  <span>google</span>
                </Button>

                {/* {!isLoginView && (
              <div className={styles.accountContainer}>
                <span>{t("AlreadyHaveAnAccount")} </span>
                <span onClick={() => onChangedLoginView(true)}>
                  {t("Sign in")}
                </span>
              </div>
            )} */}
              </Form.Item>
            </Form>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;
